/**
 * 离线演示控制条（?demo=1 / MockRoomBackend）：
 * - 一键开赛（start-hud）：辩手通过加入房间进入作战台时，Mock 后端无法自行开赛，
 *   这里显式降级补建本地权威房间（见 common/demoBootstrap.ts）；
 * - 推进环节 / 暂停 / 强制揭示 / 重置，便于本地演示与 E2E 验收。
 */
import type { MatchPhase } from '@zongheng/core-schema';
import { Badge, Button, cx } from '../ui/primitives.tsx';

export type DemoAction = 'start' | 'advance' | 'pause' | 'resume' | 'reveal' | 'reset';

export function DemoControls({
  isDemo,
  phase,
  stageIndex,
  busy,
  onAction,
}: {
  isDemo: boolean;
  phase: MatchPhase;
  stageIndex: number;
  busy: boolean;
  onAction: (action: DemoAction) => void;
}) {
  const isLobby = phase === 'LOBBY' || stageIndex === 0;
  const finished = phase === 'FINISHED';
  return (
    <section
      data-testid="hud-demo-controls"
      aria-label="演示控制"
      className={cx(
        'rounded-xl border p-3 backdrop-blur',
        isDemo ? 'border-double-blind/30 bg-double-blind/5' : 'border-white/10 bg-bg-surface/80',
      )}
    >
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-sm font-semibold tracking-wide text-slate-100">演武控制</h2>
        <Badge tone={isDemo ? 'blind' : 'neutral'}>{isDemo ? '本地 Mock' : '真实房间'}</Badge>
      </div>

      {!isDemo && isLobby && (
        <p className="text-[11px] leading-relaxed text-slate-500">
          已加入房间，等待教师端开赛。教师开始比赛后本页会自动同步环节与双表时钟。
        </p>
      )}

      {isDemo && (
        <div className="flex flex-wrap gap-2">
          {isLobby && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => onAction('start')}
              disabled={busy}
              testId="start-hud"
              title="创建本地演示房间并立即开赛"
            >
              开始演示比赛
            </Button>
          )}
          {!isLobby && !finished && (
            <>
              <Button size="sm" onClick={() => onAction('advance')} testId="hud-demo-advance">
                推进环节
              </Button>
              {phase === 'PAUSED' ? (
                <Button size="sm" variant="success" onClick={() => onAction('resume')} testId="hud-demo-pause">
                  继续
                </Button>
              ) : (
                <Button size="sm" onClick={() => onAction('pause')} testId="hud-demo-pause">
                  暂停
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => onAction('reveal')} testId="hud-demo-reveal">
                强制揭示
              </Button>
            </>
          )}
          {(finished || !isLobby) && (
            <Button size="sm" variant="danger" onClick={() => onAction('reset')} testId="hud-demo-reset">
              重置演示
            </Button>
          )}
        </div>
      )}

      <p className="mt-2 text-[10px] leading-relaxed text-slate-500">
        演示模式数据全部来自本地 Mock 后端，不联网；真实对练由教师端权威状态机驱动。
      </p>
    </section>
  );
}
